/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CalculateOutlined,
  CheckBox,
  CheckBoxOutlineBlank,
  Close,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import _ from 'lodash';

import adminFormStyles from '../shared/styles/adminForm.styles';
import colors from '../colors.styles';
import CommonUtils from '../shared/utils/common.utils';
import FormValidationsUtils from '../shared/utils/formValidations.utils';
import { initTargeting } from './TemplateTargeting.utils';
import StoresApi from '../stores/Stores.api';

const DELIMITER = ',';
const { arrayToString, stringToArray } = CommonUtils;
const { allowedChars } = FormValidationsUtils;

const styles = {
  sectionTitle: adminFormStyles.sectionTitle,
  optionalLabel: adminFormStyles.optionalLabel,
  sectionDescription: adminFormStyles.sectionDescription,
  alert: {
    alignItems: 'flex-start',
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '12px',
    color: '#92400e',
    fontSize: '14px',
    py: 0.5,
    '& .MuiAlert-icon': { color: '#b45309', mt: 0.25 },
  },
  targetingRow: {
    borderBottom: '1px solid #e9ecee',
    minHeight: 64,
    py: 1.25,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  targetingLabel: {
    color: '#041f41',
    flex: '0 0 140px',
    fontSize: '14px',
    fontWeight: 700,
  },
  addStateButton: {
    borderColor: '#a7b1c0',
    borderRadius: '20px',
    borderStyle: 'dashed',
    color: colors.base.walmart_blue,
    fontSize: '14px',
    fontWeight: 700,
    minHeight: 42,
    px: 2,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: colors.base.barely_blue,
      borderColor: colors.base.walmart_blue,
    },
  },
  addIcon: {
    fontSize: '16px !important',
  },
  stateChip: {
    backgroundColor: colors.base.walmart_blue,
    borderRadius: '20px',
    color: colors.base.light,
    fontSize: '14px',
    fontWeight: 700,
    '& .MuiChip-deleteIcon': {
      color: colors.base.light,
      fontSize: '16px',
      '&:hover': {
        color: colors.base.light,
      },
    },
    '&.MuiAutocomplete-tag': {
      margin: '0px 3px',
    },
  },
  compactInput: (width) => ({
    width,
    maxWidth: '100%',
    ...adminFormStyles.field,
    '& .MuiOutlinedInput-input': {
      padding: '10px 14px',
    },
  }),
  calculateButton: {
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    color: colors.base.walmart_blue,
    fontSize: '14px',
    fontWeight: 700,
    justifyContent: 'flex-start',
    minHeight: 48,
    px: 1.5,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: colors.base.barely_blue,
      borderColor: '#dbe2e6',
    },
  },
  statePopover: {
    p: 2,
    width: 220,
  },
};

export default function TemplateTargetingForm({
  initialTargeting,
  onStateChanged,
  isValidating,
}) {
  const [targeting, setTargeting] = useState(
    initialTargeting || initTargeting()
  );
  const [touched, setTouched] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const onStateChangedRef = useRef(onStateChanged);
  const [stateOptions, setStateOptions] = useState([]);

  useEffect(() => {
    async function fetchStates() {
      try {
        const storeDetails = await StoresApi.getStores();
        const uniqueStates = [
          ...new Set(
            storeDetails.data.map(({ state }) => state).filter(Boolean)
          ),
        ];
        setStateOptions(uniqueStates);
      } catch (error) {
        console.error('Error fetching states:', error);
      }
    }
    fetchStates();
  }, []);

  useEffect(() => {
    onStateChangedRef.current = onStateChanged;
  }, [onStateChanged]);

  function updateTargeting(key, value) {
    setTouched((prev) => ({ ...prev, [key]: true }));
    setTargeting((prev) => ({ ...prev, [key]: value }));
  }

  function validate(nextTargeting = targeting) {
    const newErrors = {};
    const onlyNums = allowedChars(`0-9 ${DELIMITER}`);
    const onlyChars = allowedChars(`a-zA-Z ${DELIMITER}`);
    const { store_number, markets, states } = nextTargeting;

    if (!onlyNums.isValid(store_number.join(DELIMITER))) {
      newErrors.store_number = onlyNums.helperText;
    }
    if (!onlyNums.isValid(markets.join(DELIMITER))) {
      newErrors.markets = onlyNums.helperText;
    }
    if (!onlyChars.isValid(states.join(DELIMITER))) {
      newErrors.states = onlyChars.helperText;
    }

    const isValid = _.isEmpty(newErrors);
    setValidationErrors(newErrors);
    return isValid;
  }

  useEffect(() => {
    onStateChangedRef.current(targeting, validate(targeting));
  }, [targeting, isValidating]);

  function fieldError(key) {
    return (touched[key] || isValidating) && validationErrors[key];
  }

  function renderStateRow() {
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={styles.targetingRow}
      >
        <Typography sx={styles.targetingLabel}>States</Typography>
        <Autocomplete
          multiple
          freeSolo
          disableCloseOnSelect
          options={stateOptions}
          value={targeting.states}
          onChange={(event, newValue) => {
            updateTargeting('states', newValue);
          }}
          sx={{ width: 300, ...adminFormStyles.field }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={targeting.states.length ? '' : '+ Add state'}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...itemProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option}
                  deleteIcon={<Close />}
                  sx={styles.stateChip}
                  {...itemProps}
                />
              );
            })
          }
          renderOption={(props, option, { selected }) => {
            const SelectionIcon = selected ? CheckBox : CheckBoxOutlineBlank;
            return (
              <li {...props}>
                <SelectionIcon
                  fontSize="small"
                  style={{
                    marginRight: 8,
                    padding: 9,
                    boxSizing: 'content-box',
                  }}
                />
                {option}
              </li>
            );
          }}
        />
      </Stack>
    );
  }

  function renderTextRow(key, label, placeholder, width, inputProps = {}) {
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={styles.targetingRow}
      >
        <Typography sx={styles.targetingLabel}>{label}</Typography>
        <TextField
          hiddenLabel
          placeholder={placeholder}
          value={arrayToString(targeting[key], DELIMITER)}
          onChange={(event) =>
            updateTargeting(key, stringToArray(event.target.value, DELIMITER))
          }
          error={Boolean(fieldError(key))}
          helperText={fieldError(key)}
          inputProps={inputProps}
          sx={styles.compactInput(width)}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing="20px">
      <Box>
        <Typography component="h2" sx={styles.sectionTitle}>
          Targeting{' '}
          <Typography component="span" sx={styles.optionalLabel}>
            optional
          </Typography>
        </Typography>
        <Typography sx={styles.sectionDescription}>
          Choose who sees this template
        </Typography>
      </Box>

      <Alert severity="warning" sx={styles.alert}>
        <strong>These filters use AND logic.</strong> A store must match{' '}
        <strong>every</strong> filter below to qualify. Adding filters narrows
        the audience, it never adds more stores.
      </Alert>

      <Stack>
        {renderStateRow()}
        {renderTextRow('regions', 'Regions', 'e.g. 2, 42, 6...', 220)}
        {renderTextRow(
          'markets',
          'Market Numbers',
          'e.g. 101, 205, 310...',
          164,
          { style: { fontVariantNumeric: 'tabular-nums' } }
        )}
        {renderTextRow(
          'store_number',
          'Store/Club Numbers',
          'e.g. 5505, 2938...',
          164,
          { style: { fontVariantNumeric: 'tabular-nums' } }
        )}
      </Stack>

      <Button
        type="button"
        fullWidth
        startIcon={<CalculateOutlined />}
        sx={styles.calculateButton}
        onClick={() => {}}
      >
        Calculate my reach
      </Button>
    </Stack>
  );
}

TemplateTargetingForm.propTypes = {
  initialTargeting: PropTypes.shape({
    regions: PropTypes.arrayOf(PropTypes.string),
    markets: PropTypes.arrayOf(PropTypes.string),
    states: PropTypes.arrayOf(PropTypes.string),
    store_number: PropTypes.arrayOf(PropTypes.string),
  }),
  onStateChanged: PropTypes.func.isRequired,
  isValidating: PropTypes.bool,
};

TemplateTargetingForm.defaultProps = {
  initialTargeting: null,
  isValidating: false,
};
